import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ShieldCheck, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/status";
import AddToOrgDialog from "@/components/AddToOrgDialog";

function OrgChips({ u }) {
  return (
    <div className="flex flex-wrap gap-1">{u.memberships.map((m) => (
      <span key={m.id} className={`text-[11px] px-1.5 py-0.5 rounded border ${m.status === "active" && m.org_lifecycle === "active" ? "bg-white" : "bg-slate-100 text-slate-500 line-through"}`} title={m.status}>
        {m.tenant_id === u.default_org_id && <Star className="w-3 h-3 inline -mt-0.5 mr-0.5 fill-amber-400 text-amber-500" />}{m.org_name} - {ROLE_LABEL[m.role] || m.role}</span>))}
      {!u.memberships.length && <span className="text-xs text-slate-400">No organization</span>}</div>
  );
}

function DefaultPick({ u, onSaved }) {
  const act = u.memberships.filter((m) => m.status === "active");
  const save = async (tid) => { try { await api.put(`/enterprise/members/${u.user_id}/default-org`, { tenant_id: tid }); toast.success("Default organization saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  if (!act.length) return <span className="text-xs text-slate-400">-</span>;
  return (
    <Select value={u.default_org_id || ""} onValueChange={save}><SelectTrigger className="h-8 w-48 text-xs" data-testid={`ent-default-${u.user_id}`}><SelectValue placeholder="Not set" /></SelectTrigger>
      <SelectContent>{act.map((m) => <SelectItem key={m.tenant_id} value={m.tenant_id}>{m.org_name}</SelectItem>)}</SelectContent></Select>
  );
}

export default function EnterpriseMembers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(null);
  const load = () => api.get("/enterprise/members").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const shown = rows.filter((u) => `${u.name} ${u.email} ${u.memberships.map((m) => m.org_name).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3" data-testid="enterprise-members">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or organization" className="pl-8 h-9" data-testid="ent-members-search" /></div>
        <span className="text-xs text-slate-500">{shown.length} of {rows.length} people. <Star className="w-3 h-3 inline fill-amber-400 text-amber-500" /> = default organization (opens first at sign-in).</span>
      </div>
      <div className="overflow-x-auto"><Table className="bg-white border rounded-xl"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Organizations & roles</TableHead><TableHead>Default organization</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>{shown.map((u) => (
          <TableRow key={u.user_id} data-testid={`ent-member-row-${u.user_id}`}>
            <TableCell className="whitespace-nowrap">{u.name}{u.super_admin && <span className="ml-2 text-[10px] uppercase px-1 rounded bg-indigo-50 text-indigo-800 border border-indigo-200"><ShieldCheck className="w-3 h-3 inline -mt-0.5" /> Super Admin</span>}
              {u.demo && <span className="ml-2 text-[10px] uppercase bg-slate-100 px-1 rounded">demo, no login</span>}</TableCell>
            <TableCell className="text-xs">{u.email}</TableCell>
            <TableCell><OrgChips u={u} /></TableCell>
            <TableCell><DefaultPick u={u} onSaved={load} /></TableCell>
            <TableCell>{!u.demo && <Button size="sm" variant="outline" onClick={() => setAdding(u)} data-testid={`ent-add-org-${u.user_id}`}>Add to organization</Button>}</TableCell>
          </TableRow>))}</TableBody></Table></div>
      <AddToOrgDialog user={adding} onClose={() => setAdding(null)} onDone={load} />
    </div>
  );
}
